package chat.amp.tim

import de.connect2x.messenger.compose.view.composeViewModule
import de.connect2x.messenger.compose.view.notifications.notificationsModule
import de.connect2x.tim.compose.platformTimStartupModule
import de.connect2x.tim.compose.timViewModuleFactory
import de.connect2x.trixnity.messenger.extensions.tim.TIMConfiguration
import de.connect2x.trixnity.messenger.extensions.tim.TIMSpec
import de.connect2x.trixnity.messenger.extensions.tim.addTIMModule
import de.connect2x.trixnity.messenger.extensions.tim.httpclient.ConfigureHttpClient
import de.connect2x.trixnity.messenger.extensions.tim.httpclient.installTimeouts
import de.connect2x.trixnity.messenger.extensions.tim.httpclient.platformConfigureHttpClient
import de.connect2x.trixnity.messenger.i18n.DefaultLanguages
import de.connect2x.trixnity.messenger.i18n.I18n
import de.connect2x.trixnity.messenger.i18n.Languages
import de.connect2x.trixnity.messenger.i18n.platformGetSystemLangModule
import de.connect2x.trixnity.messenger.multi.MatrixMultiMessengerConfiguration
import de.connect2x.trixnity.messenger.platformMatrixMessengerSettingsHolderModule
import de.connect2x.trixnity.messenger.util.RootPath
import io.ktor.http.Url
import org.koin.dsl.module
import de.connect2x.trixnity.messenger.extensions.tim.session.PlatformSessionSpec
import de.connect2x.trixnity.messenger.extensions.tim.session.SessionTimeoutAction
import de.connect2x.trixnity.messenger.extensions.tim.session.SessionTimeoutResetTrigger
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours

fun ampConfiguration(
    timSpec: TIMSpec = BuildConfig.spec,
    overrideVzdUrl: Url? = null,
    overrideIdpUrl: Url? = null,
    customConfig: MatrixMultiMessengerConfiguration.() -> Unit = {},
    customTIMConfig: TIMConfiguration.() -> Unit = {},
): MatrixMultiMessengerConfiguration.() -> Unit = multiMessengerConfig@{
    val notificationsDebugEnabled = BuildConfig.flavor == Flavor.DEV
    appName = BuildConfig.appName
    appId = BuildConfig.appId
    privacyInfo = Brand.urls.privacy
    imprint = Brand.urls.imprint
    licenses = BuildConfig.licenses
    // sendLogsEmailAddress = ""
    pushUrl = Brand.urls.push
    urlProtocol = BuildConfig.appId

    modulesFactories += listOf(
        { composeViewModule(null) },
        { notificationsModule(this@multiMessengerConfig, notificationsDebugEnabled) },
        // TODO this needs to be removed and fixed, as there is no MatrixMessengerSettingsHolderImpl at MultiMessenger level!
        ::platformMatrixMessengerSettingsHolderModule,
        // TODO there should be a more clean way for I18n
        ::platformGetSystemLangModule,
        {
            module {
                single<Languages> { DefaultLanguages }
                single<I18n> { object : I18n(get(), get(), get(), get()) {} }
            }
        },
        timViewModuleFactory(timSpec),
        ::ampThemeModule,
    )

    when (BuildConfig.flavor) {
        Flavor.PROD -> {}
        Flavor.REF -> {}
        Flavor.DEV -> {
            modulesFactories += {
                module {
                    val devRootPath = getDevRootPath()
                    if (devRootPath != null) single<RootPath> { devRootPath }
                }
            }
        }
    }

    messengerConfiguration messengerConfig@{
        // needed for TIM
        defaultPresenceIsPublic = false
        defaultReadMarkerIsPublic = false
        defaultTypingIsPublic = false
        useRefreshTokens = true

        val colors = listOf(
            0xAAB749C7,
            0xAAC77849,
            0xAA59C749,
        )
        generateInitialAccountColor = { alreadyUsedColors: Set<Long> ->
            colors.firstOrNull { alreadyUsedColors.contains(it).not() } ?: 0xFF333333
        }

        modulesFactories += listOf(
            { composeViewModule(this) },
            { notificationsModule(this@messengerConfig, notificationsDebugEnabled) },
            timViewModuleFactory(timSpec),
            ::ampThemeModule,
            ::platformTimStartupModule,
            ::ampModule
        )
    }

    addTIMModule(timSpec) {
        clientId = Brand.tim.clientId
        // the gematik manufacturer id identifies us, not the customer - the same for all brands
        manufacturerId = "AWESO"
        versionCheckUrl = Url(Brand.urls.versionCheck)
        version = BuildConfig.version

        // TIMConfiguration flavors
        when (BuildConfig.flavor) {
            Flavor.PROD -> {
                //version = BuildConfig.version.toGematikVersion()
                vzdUrl = overrideVzdUrl ?: TIMConfiguration.DefaultVzdUrls.production
                idpUrl = overrideIdpUrl ?: TIMConfiguration.DefaultIdpUrls.production
            }

            Flavor.REF -> {
                //version = BuildConfig.version.toGematikVersion() + "-REF"
                vzdUrl = overrideVzdUrl ?: TIMConfiguration.DefaultVzdUrls.reference
                idpUrl = overrideIdpUrl ?: TIMConfiguration.DefaultIdpUrls.reference
            }

            Flavor.DEV -> {
                //version = BuildConfig.version.toGematikVersion() + "-DEV"
                vzdUrl = overrideVzdUrl ?: TIMConfiguration.DefaultVzdUrls.reference
                idpUrl = overrideIdpUrl ?: TIMConfiguration.DefaultIdpUrls.reference
                disableFederationCheck = true
                // TODO needs to be updated every 30 days
                customVzdSearchAccessToken =
                    "eyJhbGciOiJIUzI1NiJ9.eyJleHAiOjE3NTMzMzk2NjMsInVzZXJuYW1lIjoibXRoaWVsZSJ9.33QoClnbzm-peQfs-kNJjY050aFjDQMIfmmPOV1CvNI"
            }
        }

        customTIMConfig()
    }

    when (BuildConfig.flavor) {
        Flavor.PROD -> {}
        Flavor.REF -> {}
        Flavor.DEV -> {
            modulesFactories += {
                module {
                    val devRootPath = getDevRootPath()
                    if (devRootPath != null) single<RootPath> { devRootPath }
                    single<ConfigureHttpClient>(createdAtStart = true) {
                        val configuration = get<MatrixMultiMessengerConfiguration>()
                        val timConfiguration = get<TIMConfiguration>()
                        val wrapped = platformConfigureHttpClient(configuration, timConfiguration) {
                            // don't set any connection spec, we want to allow TLS in DEV
                        }
                        ConfigureHttpClient {
                            wrapped.configure()
                            configuration.httpClientConfig = {
                                installTimeouts(timConfiguration)
                            }
                        }.apply { configure() }
                    }
                }
            }
        }
    }

    // change maximum duration until auto logout for webclient
    messengerConfiguration {
        modulesFactories += {
            module {
                single<PlatformSessionSpec> {
                    PlatformSessionSpec(
                        sessionTimeoutResetTrigger = SessionTimeoutResetTrigger.ACTIVITY,
                        sessionTimeoutAction = SessionTimeoutAction.LOGOUT,
                        defaultSessionTimeout = 1.hours,
                        maxSessionTimeout = 183.days,
                        )
                }
            }
        }
    }

    messengerConfiguration {
        downloadsDisabled = Brand.features.downloadsDisabled
        when (BuildConfig.flavor) {
            Flavor.PROD -> {}
            Flavor.REF -> {}
            Flavor.DEV -> {
                defaultHomeServer = "demo.ti-messenger.chat"
            }
        }
    }

    customConfig()
}

private fun String.toGematikVersion(): String {
    val sv = this.split('.')
    return "${sv[0]}.0.${sv[1]}.${sv[2]}"
}

internal expect fun getDevRootPath(): RootPath?
